import { getProxyUrl } from '@proxies/index'
import {
  CatalogTextureRecord,
  fetchCatalogTexturesForAcabado,
  findMatchingCatalogTexture,
  hasCatalogTexturesCache,
  safeParseJsonResponse,
  StoredTextureLike,
} from '@utils/sceneObjects/linked/textureCatalogApi'

export type AcabadoZoneEntry = {
  zona?: string
  modelo?: string
  acabados?: Array<{
    acabado?: string
    idAcabadoUnidad?: number
    idAcabadoUnidadModeloAcabado?: number
  }>
}

export type TextureCatalogMatch = {
  catalogTexture: CatalogTextureRecord
  acabadoFamilia?: string
  idAcabadoUnidadModeloAcabado: number
  /** idAcabadoUnidad — slot de zona para getacabadozonamodeloacabadoinfo */
  zoneSlotId?: number
}

type BuscarTexturaEnUnidadRow = {
  zona?: string
  idAcabadoUnidad?: number
  idAcabadoUnidadModeloAcabado?: number
}

const zoneAcabadoInfoCache = new Map<number, AcabadoZoneEntry[]>()
const buscarTexturaEnUnidadCache = new Map<string, BuscarTexturaEnUnidadRow[]>()

export function clearTextureCatalogMatchCaches(): void {
  zoneAcabadoInfoCache.clear()
  buscarTexturaEnUnidadCache.clear()
}

export function hasZoneAcabadoInfoCache(linkedTextureID: number): boolean {
  return zoneAcabadoInfoCache.has(linkedTextureID)
}

export function zoneNamesMatch(a?: string, b?: string): boolean {
  const normalize = (value?: string) =>
    String(value ?? '')
      .toLowerCase()
      .trim()

  const za = normalize(a)
  const zb = normalize(b)
  if (!za || !zb) return false

  return (
    za === zb ||
    za.includes(zb) ||
    zb.includes(za) ||
    (za === 'armazon' && zb.includes('puerta')) ||
    (zb === 'armazon' && za.includes('puerta'))
  )
}

export function findZoneEntryForTexture(
  zoneList: AcabadoZoneEntry[],
  texture: StoredTextureLike,
): AcabadoZoneEntry | undefined {
  return zoneList.find((entry) => zoneNamesMatch(entry.zona, texture.zoneDisplayName))
}

function findAcabadoFamiliaForId(
  zoneEntry: AcabadoZoneEntry | undefined,
  acabadoId: number,
): string | undefined {
  return zoneEntry?.acabados?.find((acabado) => {
    const id = acabado.idAcabadoUnidadModeloAcabado || acabado.idAcabadoUnidad
    return id === acabadoId
  })?.acabado
}

export async function fetchZoneAcabadoInfo(linkedTextureID: number): Promise<AcabadoZoneEntry[]> {
  const cached = zoneAcabadoInfoCache.get(linkedTextureID)
  if (cached) return cached

  const response = await fetch(
    getProxyUrl(`api/Acabados/getacabadozonamodeloacabadoinfo/${linkedTextureID}`),
    {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept-Language': 'ES',
        Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
      },
    },
  )

  if (!response.ok || response.status === 204) {
    zoneAcabadoInfoCache.set(linkedTextureID, [])
    return []
  }

  const json = await safeParseJsonResponse(response)
  if (!json) {
    zoneAcabadoInfoCache.set(linkedTextureID, [])
    return []
  }

  const data = ((json as { data?: unknown })?.data ?? json) as AcabadoZoneEntry[]
  const list = Array.isArray(data) ? data : []
  zoneAcabadoInfoCache.set(linkedTextureID, list)
  return list
}

export async function fetchBuscarTexturaEnUnidad(
  idUnidades: number,
  searchID: number,
): Promise<BuscarTexturaEnUnidadRow[]> {
  const cacheKey = `${idUnidades}:${searchID}`
  const cached = buscarTexturaEnUnidadCache.get(cacheKey)
  if (cached) return cached

  const response = await fetch(
    getProxyUrl(`api/acabados/buscartexturaenunidad/${idUnidades}/${searchID}`),
    {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept-Language': 'ES',
        Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
      },
    },
  )

  if (!response.ok || response.status === 204) {
    buscarTexturaEnUnidadCache.set(cacheKey, [])
    return []
  }

  const json = await safeParseJsonResponse(response)
  if (!json) {
    buscarTexturaEnUnidadCache.set(cacheKey, [])
    return []
  }

  const rows = (Array.isArray(json) ? json : (json as { data?: unknown })?.data ?? []) as BuscarTexturaEnUnidadRow[]
  buscarTexturaEnUnidadCache.set(cacheKey, rows)
  return rows
}

function getCatalogAcabadoId(
  acabado: NonNullable<AcabadoZoneEntry['acabados']>[number],
): number | null {
  const id = acabado.idAcabadoUnidadModeloAcabado || acabado.idAcabadoUnidad
  return id && id > 0 ? id : null
}

export async function recoverZoneSlotId(
  texture: StoredTextureLike,
  idUnidades?: number,
  onApiCall?: () => void,
): Promise<{ zoneSlotId: number | null; zoneList: AcabadoZoneEntry[] }> {
  const candidates = new Set<number>()

  if (typeof texture.linkedTextureID === 'number' && texture.linkedTextureID > 0) {
    candidates.add(texture.linkedTextureID)
  }

  const compositeSuffix = texture.idAcabadoUnidadModeloAcabado?.split('_').pop()
  const compositeNum = Number(compositeSuffix)
  if (Number.isFinite(compositeNum) && compositeNum > 0) {
    candidates.add(compositeNum)
  }

  for (const candidate of candidates) {
    const wasCached = hasZoneAcabadoInfoCache(candidate)
    const zoneList = await fetchZoneAcabadoInfo(candidate)
    if (!wasCached) onApiCall?.()

    const zoneEntry = findZoneEntryForTexture(zoneList, texture)
    if (zoneList.length > 0 && zoneEntry?.acabados?.length) {
      return { zoneSlotId: candidate, zoneList }
    }
  }

  if (idUnidades && typeof texture.searchID === 'number' && texture.searchID > 0) {
    const rows = await fetchBuscarTexturaEnUnidad(idUnidades, texture.searchID)
    const zoneHits = rows.filter((row) => zoneNamesMatch(row.zona, texture.zoneDisplayName))
    const hit = zoneHits[0]

    if (hit?.idAcabadoUnidad) {
      const wasCached = hasZoneAcabadoInfoCache(hit.idAcabadoUnidad)
      const zoneList = await fetchZoneAcabadoInfo(hit.idAcabadoUnidad)
      if (!wasCached) onApiCall?.()

      if (zoneList.length > 0 && findZoneEntryForTexture(zoneList, texture)) {
        return { zoneSlotId: hit.idAcabadoUnidad, zoneList }
      }
    }
  }

  return {
    zoneSlotId:
      typeof texture.linkedTextureID === 'number' && texture.linkedTextureID > 0
        ? texture.linkedTextureID
        : null,
    zoneList: [],
  }
}

async function getCatalogForAcabado(
  acabadoId: number,
  catalogByAcabadoId: Map<number, CatalogTextureRecord[]>,
  onApiCall?: () => void,
): Promise<CatalogTextureRecord[]> {
  const cached = catalogByAcabadoId.get(acabadoId)
  if (cached) return cached

  const wasCached = hasCatalogTexturesCache(acabadoId)
  const catalog = await fetchCatalogTexturesForAcabado(acabadoId)
  catalogByAcabadoId.set(acabadoId, catalog)
  if (!wasCached) onApiCall?.()
  return catalog
}

export async function resolveAcabadoForAppliedTexture(
  texture: StoredTextureLike,
  acabados: NonNullable<AcabadoZoneEntry['acabados']>,
  catalogByAcabadoId = new Map<number, CatalogTextureRecord[]>(),
  onApiCall?: () => void,
): Promise<{
  acabado: NonNullable<AcabadoZoneEntry['acabados']>[number]
  catalogTexture: CatalogTextureRecord
  idAcabadoUnidadModeloAcabado: number
} | null> {
  const preferredName = String(texture.acabado ?? '').trim().toLowerCase()
  const ordered = preferredName
    ? [
        ...acabados.filter((entry) => String(entry.acabado ?? '').trim().toLowerCase() === preferredName),
        ...acabados.filter((entry) => String(entry.acabado ?? '').trim().toLowerCase() !== preferredName),
      ]
    : acabados

  for (const acabado of ordered) {
    const acabadoId = getCatalogAcabadoId(acabado)
    if (!acabadoId) continue

    const catalog = await getCatalogForAcabado(acabadoId, catalogByAcabadoId, onApiCall)
    const match = findMatchingCatalogTexture(texture, catalog)
    if (match) {
      return { acabado, catalogTexture: match, idAcabadoUnidadModeloAcabado: acabadoId }
    }
  }

  return null
}

export async function resolveTextureCatalogMatch(
  texture: StoredTextureLike,
  options: {
    idUnidades?: number
    zoneList?: AcabadoZoneEntry[]
    catalogByAcabadoId: Map<number, CatalogTextureRecord[]>
    onApiCall?: () => void
  },
): Promise<TextureCatalogMatch | null> {
  let zoneList = options.zoneList ?? []
  let zoneSlotId =
    typeof texture.linkedTextureID === 'number' && texture.linkedTextureID > 0
      ? texture.linkedTextureID
      : null

  let zoneEntry = zoneList.length ? findZoneEntryForTexture(zoneList, texture) : undefined

  if (!zoneEntry) {
    const recovered = await recoverZoneSlotId(texture, options.idUnidades, options.onApiCall)
    if (recovered.zoneList.length) {
      zoneList = recovered.zoneList
      zoneSlotId = recovered.zoneSlotId
      zoneEntry = findZoneEntryForTexture(zoneList, texture)
    } else if (recovered.zoneSlotId) {
      zoneSlotId = recovered.zoneSlotId
    }
  }

  if (zoneEntry?.acabados?.length) {
    const resolved = await resolveAcabadoForAppliedTexture(
      texture,
      zoneEntry.acabados,
      options.catalogByAcabadoId,
      options.onApiCall,
    )
    if (resolved) {
      return {
        catalogTexture: resolved.catalogTexture,
        acabadoFamilia: resolved.acabado.acabado,
        idAcabadoUnidadModeloAcabado: resolved.idAcabadoUnidadModeloAcabado,
        zoneSlotId: zoneSlotId || undefined,
      }
    }
  }

  if (options.idUnidades && typeof texture.searchID === 'number' && texture.searchID > 0) {
    const rows = await fetchBuscarTexturaEnUnidad(options.idUnidades, texture.searchID)
    const zoneHits = rows.filter((row) => zoneNamesMatch(row.zona, texture.zoneDisplayName))
    const candidates = zoneHits.length > 0 ? zoneHits : rows

    for (const row of candidates) {
      const catalogAcabadoId = row.idAcabadoUnidadModeloAcabado || row.idAcabadoUnidad
      if (!catalogAcabadoId) continue

      const catalog = await getCatalogForAcabado(
        catalogAcabadoId,
        options.catalogByAcabadoId,
        options.onApiCall,
      )
      const match = findMatchingCatalogTexture(texture, catalog)
      if (match) {
        return {
          catalogTexture: match,
          acabadoFamilia: findAcabadoFamiliaForId(zoneEntry, catalogAcabadoId),
          idAcabadoUnidadModeloAcabado: catalogAcabadoId,
          zoneSlotId: row.idAcabadoUnidad || zoneSlotId || undefined,
        }
      }
    }
  }

  return null
}
